import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import * as authApi from '../api/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const u = await authApi.me();
      setUser(u);
      return u;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  // Attempt to restore an existing session on first load.
  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  /** credentials: { username, password, remember? } — email is not a credential. */
  const login = useCallback(async (credentials) => {
    const u = await authApi.login(credentials);
    // Re-fetch via /me to guarantee full, fresh user (incl. member relation).
    const fresh = await authApi.me().catch(() => u);
    setUser(fresh || u);
    return fresh || u;
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    role: user?.role || 'visitor',
    isMember: user?.role === 'member' || user?.role === 'administrator',
    isAdmin: user?.role === 'administrator',
    login,
    logout,
    refresh,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
